import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VendorMonitoring = () => {
  const vendorSystems = [
    {
      id: 1,
      name: 'Global Airport Services API',
      status: 'healthy',
      uptime: 99.8,
      lastSync: '2 min ago',
      responseTime: 145,
      syncedBookings: 1247,
      failedTransactions: 0,
      apiVersion: 'v2.1'
    },
    {
      id: 2,
      name: 'SecureTravel Protection API',
      status: 'healthy',
      uptime: 99.9,
      lastSync: '5 min ago',
      responseTime: 89,
      syncedBookings: 856,
      failedTransactions: 0,
      apiVersion: 'v3.0'
    },
    {
      id: 3,
      name: 'Elite Dining Network API',
      status: 'warning',
      uptime: 98.5,
      lastSync: '15 min ago',
      responseTime: 234,
      syncedBookings: 2134,
      failedTransactions: 3,
      apiVersion: 'v1.8'
    },
    {
      id: 4,
      name: 'MediCare International API',
      status: 'healthy',
      uptime: 99.7,
      lastSync: '3 min ago',
      responseTime: 167,
      syncedBookings: 645,
      failedTransactions: 0,
      apiVersion: 'v2.5'
    },
    {
      id: 5,
      name: 'Premium Airport Concierge API',
      status: 'error',
      uptime: 95.2,
      lastSync: '45 min ago',
      responseTime: 0,
      syncedBookings: 423,
      failedTransactions: 12,
      apiVersion: 'v1.5'
    }
  ];

  const recentAlerts = [
    {
      id: 1,
      vendor: 'Premium Airport Concierge',
      type: 'error',
      message: 'API connection timeout - multiple failed requests',
      timestamp: '10 min ago',
      severity: 'high'
    },
    {
      id: 2,
      vendor: 'Elite Dining Network',
      type: 'warning',
      message: 'Slow response time detected - average 234ms',
      timestamp: '25 min ago',
      severity: 'medium'
    },
    {
      id: 3,
      vendor: 'Global Airport Services',
      type: 'info',
      message: 'Scheduled maintenance completed successfully',
      timestamp: '1 hour ago',
      severity: 'low'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-success bg-success/10 border-success/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'error': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      {/* Monitoring Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Activity" size={24} className="text-success" />
            <span className="text-2xl font-bold text-foreground">{vendorSystems?.filter(v => v?.status === 'healthy')?.length}/{vendorSystems?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Systems Healthy</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Zap" size={24} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">99.2%</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Uptime</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={24} className="text-accent" />
            <span className="text-2xl font-bold text-foreground">159ms</span>
          </div>
          <p className="text-sm text-muted-foreground">Avg Response</p>
        </div>
        <div className="glass-container rounded-xl border border-border/50 p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertTriangle" size={24} className="text-warning" />
            <span className="text-2xl font-bold text-foreground">{recentAlerts?.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">Active Alerts</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendor Systems */}
        <div className="lg:col-span-2 glass-container rounded-xl border border-border/50">
          <div className="p-6 border-b border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Vendor API Monitoring</h3>
                <p className="text-sm text-muted-foreground mt-1">Real-time system health and performance</p>
              </div>
              <Button variant="outline" size="sm" iconName="RefreshCw">
                Refresh
              </Button>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {vendorSystems?.map((system) => (
              <div
                key={system?.id}
                className="p-4 rounded-lg border border-border/30 hover:bg-white/5 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-foreground">{system?.name}</h4>
                      <span className="text-xs text-muted-foreground bg-muted/20 px-2 py-0.5 rounded">
                        {system?.apiVersion}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs font-medium ${getStatusColor(system?.status)}`}>
                        <Icon name={getStatusIcon(system?.status)} size={12} />
                        <span>{system?.status}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Last sync: {system?.lastSync}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Uptime</p>
                    <p className="text-sm font-medium text-foreground">{system?.uptime}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Response</p>
                    <p className="text-sm font-medium text-foreground">{system?.responseTime}ms</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Synced</p>
                    <p className="text-sm font-medium text-foreground">{system?.syncedBookings}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Failed</p>
                    <p className={`text-sm font-medium ${system?.failedTransactions > 0 ? 'text-error' : 'text-success'}`}>
                      {system?.failedTransactions}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-border/30">
                  <Button variant="ghost" size="xs" iconName="Activity">
                    View Metrics
                  </Button>
                  <Button variant="ghost" size="xs" iconName="Settings">
                    Configure
                  </Button>
                  <Button variant="ghost" size="xs" iconName="TestTube">
                    Test Connection
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Alerts */}
        <div className="glass-container rounded-xl border border-border/50">
          <div className="p-6 border-b border-border/50">
            <h3 className="text-lg font-semibold text-foreground">Recent Alerts</h3>
            <p className="text-sm text-muted-foreground mt-1">System notifications</p>
          </div>
          <div className="p-6 space-y-4">
            {recentAlerts?.map((alert) => (
              <div
                key={alert?.id}
                className="p-4 rounded-lg border border-border/30 hover:bg-white/5 transition-all duration-200"
              >
                <div className="flex items-start space-x-3 mb-2">
                  <Icon 
                    name={alert?.type === 'error' ? 'XCircle' : alert?.type === 'warning' ? 'AlertTriangle' : 'Info'} 
                    size={16} 
                    className={getSeverityColor(alert?.severity)} 
                  />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-foreground mb-1">{alert?.vendor}</h4>
                    <p className="text-xs text-muted-foreground mb-2">{alert?.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{alert?.timestamp}</span>
                      <span className={`text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                        {alert?.severity} priority
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button variant="outline" size="sm" className="w-full" iconName="Bell">
              View All Alerts
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorMonitoring;